import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { CallMonitorPanel } from "@/components/call-monitor/CallMonitorPanel";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Calendar,
  TrendingUp,
  Sparkles,
  Send,
  Edit2,
  CheckCircle2,
  Clock,
  Plus,
  ExternalLink,
  Linkedin,
  Globe,
  Save,
  X,
  Flame,
  ThermometerSun,
  Snowflake,
  Target,
  User,
  Building2,
  Shield,
  Lightbulb,
  Loader2,
  MessageSquare,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Lead {
  id: string;
  status: string;
  contact_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  property_address: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  estimated_value: number | null;
  source: string | null;
  notes: string | null;
  created_at: string | null;
  converted_to_job_id: string | null;
  lead_temperature?: string | null;
  lead_source?: string | null;
  win_probability?: number | null;
  expected_close_date?: string | null;
  company_name?: string | null;
  job_title?: string | null;
  pain_points?: string | null;
  budget_qualified?: boolean;
  decision_maker?: boolean;
  linkedin_url?: string | null;
  website_url?: string | null;
  last_contact_date?: string | null;
  contact_count?: number;
}

interface Activity {
  id: string;
  type: string;
  description: string;
  created_at: string;
  metadata?: any;
}

interface Task {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date?: string | null;
  created_at: string;
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    new: "bg-blue-500 text-white",
    contacted: "bg-purple-500 text-white",
    qualified: "bg-green-500 text-white",
    proposal_sent: "bg-orange-500 text-white",
    won: "bg-emerald-500 text-white",
    lost: "bg-red-500 text-white",
  };
  return colors[status] || "bg-muted";
};

const getTemperatureIcon = (temp: string) => {
  if (temp === "hot") return <Flame className="h-4 w-4" />;
  if (temp === "warm") return <ThermometerSun className="h-4 w-4" />;
  return <Snowflake className="h-4 w-4" />;
};

const getTemperatureColor = (temp: string) => {
  if (temp === "hot") return "bg-red-100 text-red-700 border-red-200";
  if (temp === "warm") return "bg-orange-100 text-orange-700 border-orange-200";
  return "bg-blue-100 text-blue-700 border-blue-200";
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { orgId } = useAuth();
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedLead, setEditedLead] = useState<Partial<Lead>>({});
  const [activities, setActivities] = useState<Activity[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [leadScore, setLeadScore] = useState<any>(null);
  const [notes, setNotes] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDueDate, setNewTaskDueDate] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("medium");
  const [addingTask, setAddingTask] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [aiEmailPrompt, setAiEmailPrompt] = useState("");
  const [draftingWithAI, setDraftingWithAI] = useState(false);

  useEffect(() => {
    if (id) {
      loadLeadData();
      loadActivities();
      loadTasks();
      loadLeadScore();
    }
  }, [id]);

  useEffect(() => {
    if (lead) {
      setEditedLead(lead);
      setNotes(lead.notes || "");
    }
  }, [lead]);

  const loadLeadData = async () => {
    try {
      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      setLead(data);
    } catch (error) {
      console.error("Error loading lead:", error);
      toast.error("Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    try {
      const { data: emailSends } = await supabase
        .from("campaign_email_sends")
        .select("*")
        .eq("lead_id", id)
        .order("created_at", { ascending: false })
        .limit(20);

      const activities: Activity[] = [];
      emailSends?.forEach((send) => {
        activities.push({
          id: send.id,
          type: "email_sent",
          description: `Email sent: ${send.subject}`,
          created_at: send.sent_at || send.created_at,
          metadata: send,
        });
        if (send.opened_at) {
          activities.push({
            id: `${send.id}-opened`,
            type: "email_opened",
            description: `Opened email: ${send.subject}`,
            created_at: send.opened_at,
            metadata: send,
          });
        }
        if (send.clicked_at) {
          activities.push({
            id: `${send.id}-clicked`,
            type: "email_clicked",
            description: `Clicked link in email: ${send.subject}`,
            created_at: send.clicked_at,
            metadata: send,
          });
        }
        if (send.replied_at) {
          activities.push({
            id: `${send.id}-replied`,
            type: "email_replied",
            description: `Replied to: ${send.subject}`,
            created_at: send.replied_at,
            metadata: { ...send, reply_body: send.reply_body },
          });
        }
      });
      activities.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setActivities(activities);
    } catch (error) {
      console.error("Error loading activities:", error);
    }
  };

  const loadTasks = async () => {
    try {
      const { data } = await supabase
        .from("tasks")
        .select("*")
        .eq("lead_id", id)
        .order("due_date", { ascending: true });
      setTasks(data || []);
    } catch (error) {
      console.error("Error loading tasks:", error);
    }
  };

  const loadLeadScore = async () => {
    try {
      const { data } = await supabase
        .from("lead_scores")
        .select("*")
        .eq("lead_id", id)
        .maybeSingle();
      setLeadScore(data);
    } catch (error) {
      console.error("Error loading lead score:", error);
    }
  };

  const handleConvertToJob = async () => {
    if (!lead) return;
    setConverting(true);
    try {
      const { data, error } = await supabase.functions.invoke("convert-lead-to-job", {
        body: { leadId: lead.id },
      });

      if (error) throw error;
      toast.success("Lead converted to job successfully");
      navigate(`/jobs/${data.jobId}`);
    } catch (error: any) {
      console.error("Error converting lead:", error);
      toast.error(error.message || "Failed to convert lead to job");
    } finally {
      setConverting(false);
    }
  };

  const handleSaveLead = async () => {
    try {
      const sanitizedLead = {
        ...editedLead,
        estimated_value: editedLead.estimated_value === null ? null : parseFloat(editedLead.estimated_value as any) || null,
        win_probability: editedLead.win_probability === null ? null : parseInt(editedLead.win_probability as any) || null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase.from("leads").update(sanitizedLead as any).eq("id", id);
      if (error) throw error;
      toast.success("Lead updated successfully");
      setEditMode(false);
      await loadLeadData();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead");
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const { error } = await supabase
        .from("leads")
        .update({ notes, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
      toast.success("Notes saved");
      setEditingNotes(false);
      await loadLeadData();
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) {
      toast.error("Task title is required");
      return;
    }
    setAddingTask(true);
    try {
      const { error } = await supabase.from("tasks").insert({
        lead_id: id,
        org_id: orgId,
        title: newTaskTitle,
        due_date: newTaskDueDate || null,
        priority: newTaskPriority,
        status: "pending",
      });
      if (error) throw error;
      toast.success("Task created");
      setNewTaskTitle("");
      setNewTaskDueDate("");
      setNewTaskPriority("medium");
      await loadTasks();
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    } finally {
      setAddingTask(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase.from("tasks").update({ status: "completed" }).eq("id", taskId);
      if (error) throw error;
      toast.success("Task marked as complete");
      await loadTasks();
    } catch (error) {
      console.error("Error completing task:", error);
      toast.error("Failed to complete task");
    }
  };

  const handleDraftEmailWithAI = async () => {
    if (!aiEmailPrompt.trim()) {
      toast.error("Please enter a prompt for the AI");
      return;
    }
    setDraftingWithAI(true);
    try {
      const { data, error } = await supabase.functions.invoke("draft-email", {
        body: {
          prompt: aiEmailPrompt,
          leadInfo: {
            name: lead?.contact_name,
            company: lead?.company_name,
            painPoints: lead?.pain_points,
          },
        },
      });
      if (error) throw error;
      setEmailSubject(data.subject || "");
      setEmailBody(data.body || "");
      toast.success("Email drafted with AI");
    } catch (error) {
      console.error("Error drafting email:", error);
      toast.error("Failed to draft email with AI");
    } finally {
      setDraftingWithAI(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSendingEmail(true);
    try {
      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          to: lead?.contact_email,
          subject: emailSubject,
          html: emailBody.replace(/\n/g, "<br>"),
          leadId: id,
        },
      });
      if (error) throw error;

      await supabase
        .from("leads")
        .update({
          last_contact_date: new Date().toISOString(),
          contact_count: (lead?.contact_count || 0) + 1,
        })
        .eq("id", id);

      toast.success("Email sent successfully");
      setEmailDialogOpen(false);
      setEmailSubject("");
      setEmailBody("");
      setAiEmailPrompt("");
      await loadActivities();
      await loadLeadData();
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "email_sent":
        return <Send className="h-4 w-4 text-blue-500" />;
      case "email_opened":
        return <Mail className="h-4 w-4 text-green-500" />;
      case "email_clicked":
        return <Zap className="h-4 w-4 text-purple-500" />;
      case "email_replied":
        return <MessageSquare className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    if (priority === "high") return "text-red-600 bg-red-50";
    if (priority === "medium") return "text-orange-600 bg-orange-50";
    return "text-blue-600 bg-blue-50";
  };

  if (loading || !lead) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/sales-hub")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Sales Hub
            </Button>
            <div className="flex gap-2">
              {!lead.converted_to_job_id && (
                <Button onClick={handleConvertToJob} disabled={converting}>
                  {converting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Convert to Job
                </Button>
              )}
              {lead.converted_to_job_id && (
                <Button onClick={() => navigate(`/jobs/${lead.converted_to_job_id}`)}>
                  View Job
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-8 w-8 text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">{lead.contact_name}</h1>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge className={cn(getStatusColor(lead.status), "uppercase text-xs")}>
                  {lead.status}
                </Badge>
                {lead.lead_temperature && (
                  <Badge variant="outline" className={cn("gap-1", getTemperatureColor(lead.lead_temperature))}>
                    {getTemperatureIcon(lead.lead_temperature)}
                    {lead.lead_temperature}
                  </Badge>
                )}
                {lead.source && (
                  <Badge variant="secondary" className="gap-1">
                    <Target className="h-3 w-3" />
                    via {lead.source}
                  </Badge>
                )}
              </div>
              {lead.property_address && (
                <p className="text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {lead.property_address}
                  {lead.property_city && `, ${lead.property_city}`}
                  {lead.property_state && `, ${lead.property_state}`}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="border-b bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex gap-3">
            {lead.contact_phone && (
              <Button variant="outline" size="sm" asChild>
                <a href={`tel:${lead.contact_phone}`}>
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </a>
              </Button>
            )}
            {lead.contact_email && (
              <Button variant="outline" size="sm" onClick={() => setEmailDialogOpen(true)}>
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)}>
              {editMode ? (
                <>
                  <X className="h-4 w-4 mr-2" />
                  Cancel Edit
                </>
              ) : (
                <>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Info
                </>
              )}
            </Button>
            {editMode && (
              <Button size="sm" onClick={handleSaveLead}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="border-b bg-background">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">
                  ${lead.estimated_value?.toLocaleString() || "—"}
                </div>
                <p className="text-xs text-muted-foreground">Estimated Value</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{lead.win_probability || "—"}%</div>
                <p className="text-xs text-muted-foreground">Win Probability</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{lead.lead_source || "—"}</div>
                <p className="text-xs text-muted-foreground">Source</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">
                  {lead.expected_close_date
                    ? new Date(lead.expected_close_date).toLocaleDateString()
                    : "—"}
                </div>
                <p className="text-xs text-muted-foreground">Expected Close</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">
                  {lead.last_contact_date
                    ? `${Math.floor((Date.now() - new Date(lead.last_contact_date).getTime()) / (1000 * 60 * 60 * 24))}d`
                    : "—"}
                </div>
                <p className="text-xs text-muted-foreground">Last Contact</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Call with Bridgette */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Call with Bridgette
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CallMonitorPanel leadId={id} mode="lead" />
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {editMode ? (
                  <>
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={editedLead.contact_name || ""}
                        onChange={(e) => setEditedLead({ ...editedLead, contact_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editedLead.contact_email || ""}
                        onChange={(e) => setEditedLead({ ...editedLead, contact_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editedLead.contact_phone || ""}
                        onChange={(e) => setEditedLead({ ...editedLead, contact_phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Company</Label>
                      <Input
                        value={editedLead.company_name || ""}
                        onChange={(e) => setEditedLead({ ...editedLead, company_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Job Title</Label>
                      <Input
                        value={editedLead.job_title || ""}
                        onChange={(e) => setEditedLead({ ...editedLead, job_title: e.target.value })}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {lead.contact_email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <a href={`mailto:${lead.contact_email}`} className="text-primary hover:underline">
                          {lead.contact_email}
                        </a>
                      </div>
                    )}
                    {lead.contact_phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <a href={`tel:${lead.contact_phone}`} className="text-primary hover:underline">
                          {lead.contact_phone}
                        </a>
                      </div>
                    )}
                    {lead.company_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{lead.company_name}</span>
                      </div>
                    )}
                    {lead.linkedin_url && (
                      <div className="flex items-center gap-2 text-sm">
                        <Linkedin className="h-4 w-4 text-muted-foreground" />
                        <a
                          href={lead.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          LinkedIn Profile
                        </a>
                      </div>
                    )}
                    {lead.website_url && (
                      <div className="flex items-center gap-2 text-sm">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <a
                          href={lead.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Website
                        </a>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Qualification Badges */}
            <Card>
              <CardHeader>
                <CardTitle>Qualification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {editMode ? (
                  <>
                    <div className="flex items-center justify-between">
                      <Label>Decision Maker</Label>
                      <Switch
                        checked={editedLead.decision_maker || false}
                        onCheckedChange={(checked) =>
                          setEditedLead({ ...editedLead, decision_maker: checked })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Budget Qualified</Label>
                      <Switch
                        checked={editedLead.budget_qualified || false}
                        onCheckedChange={(checked) =>
                          setEditedLead({ ...editedLead, budget_qualified: checked })
                        }
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <Badge variant={lead.decision_maker ? "default" : "secondary"} className="w-full justify-center">
                      <Shield className="h-3 w-3 mr-1" />
                      {lead.decision_maker ? "Decision Maker" : "Not Decision Maker"}
                    </Badge>
                    <Badge variant={lead.budget_qualified ? "default" : "secondary"} className="w-full justify-center">
                      <DollarSign className="h-3 w-3 mr-1" />
                      {lead.budget_qualified ? "Budget Qualified" : "Budget Not Qualified"}
                    </Badge>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="tasks">Tasks</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                {/* AI Intelligence */}
                {leadScore && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-primary" />
                        AI Intelligence
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Lead Score</span>
                        <span className="text-2xl font-bold">{leadScore.score}/100</span>
                      </div>
                      <Progress value={leadScore.score} className="h-2" />
                      {leadScore.insights && (
                        <div className="text-sm text-muted-foreground bg-muted p-4 rounded-lg">
                          {leadScore.insights}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Notes */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Notes</CardTitle>
                    {!editingNotes && (
                      <Button variant="ghost" size="sm" onClick={() => setEditingNotes(true)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {editingNotes ? (
                      <div className="space-y-3">
                        <Textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={6}
                          placeholder="Add notes about this lead..."
                        />
                        <div className="flex gap-2">
                          <Button onClick={handleSaveNotes} disabled={savingNotes}>
                            {savingNotes ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Save Notes
                          </Button>
                          <Button variant="outline" onClick={() => setEditingNotes(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{notes || "No notes yet."}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Pain Points */}
                {(lead.pain_points || editMode) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-primary" />
                        Pain Points
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {editMode ? (
                        <Textarea
                          value={editedLead.pain_points || ""}
                          onChange={(e) => setEditedLead({ ...editedLead, pain_points: e.target.value })}
                          rows={4}
                          placeholder="What challenges is this lead facing?"
                        />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{lead.pain_points}</p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="activity" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Email Activity Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px] pr-4">
                      {activities.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No activity yet</p>
                      ) : (
                        <div className="space-y-4">
                          {activities.map((activity) => (
                            <div key={activity.id} className="flex gap-3 pb-4 border-b last:border-0">
                              <div className="mt-1">{getActivityIcon(activity.type)}</div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">{activity.description}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(activity.created_at).toLocaleString()}
                                </p>
                                {activity.type === "email_replied" && activity.metadata?.reply_body && (
                                  <div className="mt-2 p-3 bg-muted rounded text-sm">
                                    {activity.metadata.reply_body}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="tasks" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Tasks</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Add Task Form */}
                    <div className="space-y-3 p-4 bg-muted rounded-lg">
                      <Input
                        placeholder="Task title"
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="date"
                          value={newTaskDueDate}
                          onChange={(e) => setNewTaskDueDate(e.target.value)}
                        />
                        <Select value={newTaskPriority} onValueChange={setNewTaskPriority}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low Priority</SelectItem>
                            <SelectItem value="medium">Medium Priority</SelectItem>
                            <SelectItem value="high">High Priority</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleAddTask} disabled={addingTask} className="w-full">
                        {addingTask ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                        Add Task
                      </Button>
                    </div>

                    {/* Task List */}
                    <ScrollArea className="h-[400px]">
                      {tasks.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No tasks yet</p>
                      ) : (
                        <div className="space-y-2">
                          {tasks.map((task) => (
                            <div
                              key={task.id}
                              className={cn(
                                "flex items-start gap-3 p-3 rounded-lg border",
                                task.status === "completed" && "opacity-60"
                              )}
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => handleCompleteTask(task.id)}
                                disabled={task.status === "completed"}
                              >
                                <CheckCircle2
                                  className={cn(
                                    "h-5 w-5",
                                    task.status === "completed" ? "text-green-600" : "text-muted-foreground"
                                  )}
                                />
                              </Button>
                              <div className="flex-1">
                                <p className={cn("text-sm font-medium", task.status === "completed" && "line-through")}>
                                  {task.title}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  {task.due_date && (
                                    <span className="text-xs text-muted-foreground">
                                      Due: {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                  )}
                                  <Badge variant="outline" className={cn("text-xs", getPriorityColor(task.priority))}>
                                    {task.priority}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Email to {lead.contact_name}</DialogTitle>
            <DialogDescription>Compose an email to this lead</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* AI Draft Assistant */}
            <div className="p-4 bg-muted rounded-lg space-y-3">
              <Label>Draft with AI</Label>
              <Input
                placeholder="e.g., Follow up on our demo, ask about their timeline..."
                value={aiEmailPrompt}
                onChange={(e) => setAiEmailPrompt(e.target.value)}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDraftEmailWithAI}
                disabled={draftingWithAI}
                className="w-full"
              >
                {draftingWithAI ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Generate Draft
              </Button>
            </div>

            <div>
              <Label>Subject</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject"
              />
            </div>
            <div>
              <Label>Body</Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={10}
                placeholder="Email body"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail}>
              {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}